import { useState } from 'react';
import {
  useApiKeys,
  useMintApiKey,
  useRevokeApiKey,
  type MintedApiKey,
} from '../../api/api-keys.js';

/**
 * Admin → API Keys
 *
 * Tenant admins mint keys here and hand them to Claude Code / CI /
 * scripts. Keys are scoped to the minting user; revoking that user's
 * platform role instantly revokes all their keys.
 *
 * The plaintext is shown once at mint time only; a clear callout tells
 * the admin to copy it immediately.
 */
export function ApiKeysAdminPage(): JSX.Element {
  const { data, isLoading, error } = useApiKeys();
  const [showMint, setShowMint] = useState(false);
  const [newlyMinted, setNewlyMinted] = useState<MintedApiKey | null>(null);
  const revoke = useRevokeApiKey();

  return (
    <>
      <header className="page-title">
        <div>
          <h1>API keys</h1>
          <p className="page-title-subtitle">
            Programmatic access for Claude Code, CI pipelines, and tenant-owned scripts. Every key
            is scoped to the minting user — revoke their platform roles and every key they minted
            loses the matching access.
          </p>
        </div>
        <div className="page-title-actions">
          <span className="badge badge--neutral">{data?.length ?? 0} keys</span>
          <button type="button" className="btn btn--primary" onClick={() => setShowMint(true)}>
            Mint new key
          </button>
        </div>
      </header>

      {isLoading && <div className="text-dim">Loading keys…</div>}
      {error && <div className="alert alert--danger">Failed to load keys.</div>}

      {newlyMinted && (
        <div className="alert alert--success" style={{ marginBottom: 'var(--space-3)' }}>
          <div style={{ marginBottom: 6 }}>
            <strong>Copy this key now — it will not be shown again.</strong>
          </div>
          <div className="api-key-plaintext-row">
            <code className="api-key-plaintext">{newlyMinted.plaintext}</code>
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={() => {
                void navigator.clipboard.writeText(newlyMinted.plaintext);
              }}
            >
              Copy
            </button>
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={() => setNewlyMinted(null)}
            >
              Dismiss
            </button>
          </div>
          <p className="text-sm" style={{ marginTop: 8, marginBottom: 0 }}>
            Use it as the <code>X-API-Key</code> header against{' '}
            <code>{apiOriginHint()}/reporting/api/*</code>.
          </p>
        </div>
      )}

      {data && data.length === 0 && !isLoading && (
        <div className="empty-state">
          <h3>No keys minted</h3>
          <p>Mint a key to enable programmatic access for Claude Code or a CI pipeline.</p>
        </div>
      )}

      {data && data.length > 0 && (
        <table className="table">
          <thead>
            <tr>
              <th>Label</th>
              <th>Prefix</th>
              <th>Created</th>
              <th>Expires</th>
              <th>Last used</th>
              <th>Usage</th>
              <th>Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {data.map((k) => {
              const active =
                !k.revokedAt && (!k.expiresAt || new Date(k.expiresAt).getTime() > Date.now());
              return (
                <tr key={k.id} style={{ opacity: active ? 1 : 0.55 }}>
                  <td>
                    <strong>{k.label}</strong>
                  </td>
                  <td>
                    <code className="text-xs">cnr_…{k.hint}</code>
                  </td>
                  <td className="text-xs text-dim">{new Date(k.createdAt).toLocaleString()}</td>
                  <td className="text-xs text-dim">
                    {k.expiresAt ? new Date(k.expiresAt).toLocaleString() : '—'}
                  </td>
                  <td className="text-xs text-dim">
                    {k.lastUsedAt ? new Date(k.lastUsedAt).toLocaleString() : 'never'}
                  </td>
                  <td className="text-xs text-mono">{k.usageCount.toLocaleString()}</td>
                  <td>
                    {k.revokedAt ? (
                      <span className="badge badge--danger">revoked</span>
                    ) : !active ? (
                      <span className="badge badge--neutral">expired</span>
                    ) : (
                      <span className="badge badge--success">active</span>
                    )}
                  </td>
                  <td>
                    {active && (
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        disabled={revoke.isPending}
                        onClick={() => {
                          if (
                            !confirm(
                              `Revoke "${k.label}"? Any client using this key will immediately stop working.`,
                            )
                          )
                            return;
                          revoke.mutate(k.id);
                        }}
                      >
                        Revoke
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {showMint && (
        <MintKeyDialog
          onClose={() => setShowMint(false)}
          onMinted={(m) => {
            setNewlyMinted(m);
            setShowMint(false);
          }}
        />
      )}
    </>
  );
}

function MintKeyDialog({
  onClose,
  onMinted,
}: {
  onClose(): void;
  onMinted(m: MintedApiKey): void;
}): JSX.Element {
  const mint = useMintApiKey();
  const [label, setLabel] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const canSubmit = label.trim().length > 0 && !mint.isPending;

  function submit(e: React.FormEvent): void {
    e.preventDefault();
    if (!canSubmit) return;
    mint.mutate(
      {
        label: label.trim(),
        ...(expiresAt ? { expiresAt: new Date(expiresAt).toISOString() } : {}),
        scopes: [],
      },
      { onSuccess: (m) => onMinted(m) },
    );
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <header className="modal-header">
          <div>
            <h2>Mint API key</h2>
            <div className="modal-subtitle">
              Scoped to you. Carries your current platform roles. Revoking any role downgrades every
              key you've minted.
            </div>
          </div>
          <button type="button" className="btn-icon" onClick={onClose} aria-label="Close">
            ×
          </button>
        </header>
        <form onSubmit={submit}>
          <div className="modal-body stack">
            <label className="field">
              <span className="field-label">Label</span>
              <input
                className="input"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="my-laptop-claude · ci-nightly"
                autoFocus
              />
              <div className="field-help">
                Shown in the list; pick something you'll recognize later.
              </div>
            </label>
            <label className="field">
              <span className="field-label">Expires (optional)</span>
              <input
                type="datetime-local"
                className="input"
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
              />
              <div className="field-help">
                Leave blank for no expiry. Enterprise policy: rotate every 90 days.
              </div>
            </label>
            {mint.error && (
              <div className="alert alert--danger">
                <pre>
                  {JSON.stringify(
                    (mint.error as Error & { body?: unknown }).body ?? String(mint.error),
                    null,
                    2,
                  )}
                </pre>
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn--ghost" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn--primary" disabled={!canSubmit}>
              {mint.isPending ? 'Minting…' : 'Mint key'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function apiOriginHint(): string {
  try {
    return window.location.origin;
  } catch {
    return 'https://<tenant>.reporting.canopy';
  }
}
